// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where a sound is (M3-30, 07 §8).
//
// 07 §8 is a table of emitters and positions, and the last line of it is the one this module
// exists for: "On flat screens, positional audio collapses to a stereo pan derived from
// playfield **x** with a modest distance attenuation from playfield **y** — enough that you hear
// the top of the playfield as further away."
//
// **The pan was already there and the distance was not**, which is the half that was missing and
// the half that does the work. A pan tells a player which side; a level tells them how far, and
// a playfield is two and a half times longer than it is wide — so on a machine where every sound
// is at the same level, the whole top half of the table is acoustically in the player's lap.
// Four and a half decibels is not much and it is enough: a bumper at the top and a slingshot at
// the bottom stop being the same distance away.
//
// **Not everything is on the playfield**, and the table says so. The knocker is inside the
// cabinet head; speech and the display come out of the backbox; music is cabinet stereo and
// ambience is the room. None of those move, and none of them is *far* — a backbox is directly
// in front of the player at eye level, so putting the top-of-playfield attenuation on a callout
// would be reading the geometry off the diagram rather than off the machine.
//
// **This is the seam M5 replaces.** In WebXR the same placement drives a `PannerNode` through
// `THREE.PositionalAudio` with an HRTF and a listener on the camera; on a flat screen it is a
// stereo pan and a gain. What both need is *where the sound is*, in the machine's own
// coordinates, which is what `Spot` is. Nothing above this module knows which one it is talking
// to.

/** Where 07 §8 puts an emitter. The machine format's `spatial` field names the same four. */
export type Place = 'device' | 'cabinet' | 'backbox' | 'none'

/**
 * A sound's placement: where it is, which side it reads as, and how much further away.
 *
 * **The three coordinates arrived at M9-48 and the native client has had them since M7-11.** The
 * header above says of `pan`/`db` alone: *"this is the seam M5 replaces"*. M5 shipped, and so did
 * M6, M7 and M8, and every sound in the browser — flat screen and headset alike — went through a
 * `StereoPannerNode`. So in WebXR the knocker inside the cabinet head, the speech out of the
 * backbox and a bumper at the far end of the playfield were all the same distance away, at the same
 * height, in the middle of the player's skull.
 *
 * `pan` and `db` are unchanged and stay the flat collapse — *the two clients agree about everything
 * a flat screen can hear*, which is what keeps the audio parity fixtures honest.
 */
export interface Spot {
  /** −1 hard left, +1 hard right. */
  readonly pan: number
  /** Attenuation for distance, dB and never positive. */
  readonly db: number
  /** Across the playfield, metres from its centre. +x is the player's right. */
  readonly x: number
  /** Up the playfield, metres from its centre. +y is away from the player. */
  readonly y: number
  /** Above the playfield surface, metres. The backbox and the cabinet are the two off it. */
  readonly z: number
}

/** Dead centre, at the player. The room, and anything not placed at all. */
export const HERE: Spot = { pan: 0, db: 0, x: 0, y: 0, z: 0 }

/**
 * How high above the playfield the backbox speaker sits, metres.
 *
 * The backbox is where the speech and the display live (07 §8). In stereo it is "the same place as
 * the cabinet"; in a headset it is above the player's eyeline, which is why a callout in VR sounds
 * like it is coming from the machine rather than from inside the player's head.
 *
 * `SoundSpace.BackboxHeight`, to the digit — a callout that came from a different height on the two
 * clients would be the same machine heard two ways.
 */
export const BACKBOX_HEIGHT = 0.42

/** How far below the playfield the cabinet's own speaker is. Music and the shove. */
export const CABINET_DROP = -0.2

/**
 * How much quieter the far end of the playfield is, dB.
 *
 * "A modest distance attenuation" (07 §8). Four and a half decibels is about a third of the
 * apparent loudness, which is what the eye already expects from something two feet further up a
 * table — and small enough that a jackpot at the top of the playfield is still a jackpot.
 */
export const FAR_DB = -4.5

/**
 * How much the stereo image narrows at the far end.
 *
 * Distance collapses width: two sources a foot apart are hard left and hard right at arm's
 * length and nearly the same place across a room. Without it, a bumper at the top of the
 * playfield pans as hard as a slingshot at the bottom, and the pan then argues with the level.
 */
export const FAR_PAN_SCALE = 0.72

/**
 * Place a sound at a point on the playfield.
 *
 * `y` runs from −`halfLength` at the player's end to +`halfLength` at the top, which is the
 * simulation's own convention — so nothing here has to know which way up a machine is.
 */
export function spotAt(x: number, y: number, halfWidth: number, halfLength: number): Spot {
  const across = halfWidth > 0 ? clamp(x / halfWidth, -1, 1) : 0
  // 0 at the player's end, 1 at the top.
  const away = halfLength > 0 ? clamp((y / halfLength + 1) / 2, 0, 1) : 0
  return {
    // `+ 0` on both, to turn a negative zero back into a zero. It changes nothing audible and it
    // makes the value comparable: `Object.is(-0, 0)` is false, so a placement at dead centre
    // would otherwise not equal the one the fixed places use.
    pan: across * (1 - (1 - FAR_PAN_SCALE) * away) + 0,
    db: FAR_DB * away + 0,
    // The point itself, unclamped and untouched: the pan and the level are a *collapse* of it, and
    // a headset wants the thing rather than the collapse. On the playfield surface, which is where
    // every device is — the two emitters that are not are the two `spotFor` names.
    x,
    y,
    z: 0,
  }
}

/**
 * Place a sound by what the machine's cue table says it is.
 *
 * 04 §12's `spatial` field, which until now nothing read: `device` puts a sound at the thing
 * that made it, and the other three are fixed points that do not move with the ball. A cue with
 * no `spatial` is treated as `device`, because the overwhelming majority of a machine's sounds
 * come from somewhere on the playfield and an author who says nothing means that.
 */
export function spotFor(place: Place | undefined, atDevice: Spot): Spot {
  switch (place) {
    // **All three used to return `HERE`, and the comment said why.** *"They differ in the image an
    // HRTF gives them — the cabinet is below, the backbox above — which is M5's to use; collapsed
    // to stereo they are the same place."* Both halves of that were right; the second half was
    // still true four milestones after M5, because nothing ever built the HRTF. They are three
    // places now, and they still collapse to the same pan and the same level, so a flat screen
    // hears exactly what it heard before.
    case 'cabinet':
      // Below the playfield and in the middle: a cabinet speaker is under the apron, which is why
      // music on a real machine comes from the player's own end of the table.
      return { pan: 0, db: 0, x: 0, y: 0, z: CABINET_DROP }
    case 'backbox':
      // Above the top of the playfield: the backbox. A callout comes from the machine's head.
      return { pan: 0, db: 0, x: 0, y: 0, z: BACKBOX_HEIGHT }
    case 'none':
      // Not placed at all — the room, the UI. The listener's own position.
      return HERE
    default:
      return atDevice
  }
}

/** Whether a string from a machine definition names a place. */
export function isPlace(value: unknown): value is Place {
  return value === 'device' || value === 'cabinet' || value === 'backbox' || value === 'none'
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
